import {
  CARE_MEDICATION_QUICK_WITH_MELATONIN_PATTERN,
  CARE_QUICK_CAPTURE_ACTIVITY_SIGNAL_PATTERN,
  CARE_QUICK_CAPTURE_BEHAVIOR_SIGNAL_PATTERN,
  CARE_QUICK_CAPTURE_FOOD_SIGNAL_PATTERN,
  CARE_QUICK_CAPTURE_MOOD_SIGNAL_PATTERN,
  CARE_SLEEP_QUICK_PATTERN,
} from '../shared/careSignalPatterns';

const MULTI_EVENT_CONNECTOR_REGEX = /\b(?:then|after|after school|after that|later|next|meanwhile)\b/i;
const DICTATION_RAMBLY_PHRASE_REGEX = /\b(?:so far|not sure|how the day will go|just wanted to say|kind of|sort of)\b/i;
const CATEGORY_SIGNAL_REGEXES = {
  medication: CARE_MEDICATION_QUICK_WITH_MELATONIN_PATTERN,
  food: CARE_QUICK_CAPTURE_FOOD_SIGNAL_PATTERN,
  sleep: CARE_SLEEP_QUICK_PATTERN,
  behavior: CARE_QUICK_CAPTURE_BEHAVIOR_SIGNAL_PATTERN,
  activity: CARE_QUICK_CAPTURE_ACTIVITY_SIGNAL_PATTERN,
  mood: CARE_QUICK_CAPTURE_MOOD_SIGNAL_PATTERN,
};

const normalizeWhitespace = (text = '') => String(text || '')
  .replace(/\r\n/g, '\n')
  .replace(/\s+/g, ' ')
  .trim();

const addSentenceBreaksForDictation = (text = '') => {
  let normalized = String(text || '').trim();
  if (!normalized) {
    return '';
  }

  normalized = normalized.replace(
    /\b(today|this morning|this afternoon|this evening|tonight|after school|later|then|after that|meanwhile|at school pickup|around bedtime|before bedtime)\b/gi,
    '. $1',
  );
  normalized = normalized.replace(/\.\s*\./g, '.');
  normalized = normalized.replace(/\s+\./g, '.');
  normalized = normalized.replace(/^\.\s*/, '');

  if (!/[.!?]$/.test(normalized)) {
    normalized = `${normalized}.`;
  }

  return normalized.replace(/\s+/g, ' ').trim();
};

const countCategorySignals = (text = '') => {
  const hits = Object.entries(CATEGORY_SIGNAL_REGEXES).filter(([, pattern]) => pattern.test(text));
  return {
    count: hits.length,
    categories: hits.map(([name]) => name),
  };
};

export const normalizeQuickCaptureTranscript = (inputText = '') => {
  const originalText = String(inputText || '');
  const trimmedOriginalText = originalText.trim();

  if (!trimmedOriginalText) {
    return {
      originalText,
      normalizedText: '',
      isLikelyDictated: false,
      hasMultipleEvents: false,
      shouldForceReview: false,
      reasons: [],
    };
  }

  const collapsedText = normalizeWhitespace(trimmedOriginalText);
  const sentenceCount = collapsedText.split(/(?<=[.!?])\s+/).filter(Boolean).length;
  const wordCount = collapsedText.split(/\s+/).filter(Boolean).length;
  const missingTerminalPunctuation = !/[.!?]$/.test(collapsedText);
  const hasWeakPunctuation = sentenceCount <= 1 && missingTerminalPunctuation && wordCount >= 3;
  const normalizedText = hasWeakPunctuation ? addSentenceBreaksForDictation(collapsedText) : collapsedText;
  const categorySignals = countCategorySignals(normalizedText);
  const isLikelyDictated = hasWeakPunctuation || (wordCount >= 10 && sentenceCount <= 1);

  const hasMultipleEvents = categorySignals.count >= 2
    || MULTI_EVENT_CONNECTOR_REGEX.test(normalizedText);

  const reasons = [];
  if (isLikelyDictated) {
    reasons.push('likely_dictated');
  }
  if (hasWeakPunctuation) {
    reasons.push('missing_punctuation');
  }
  if (categorySignals.count >= 2) {
    reasons.push('multiple_categories');
  }
  if (MULTI_EVENT_CONNECTOR_REGEX.test(normalizedText)) {
    reasons.push('event_transition_language');
  }
  if (DICTATION_RAMBLY_PHRASE_REGEX.test(normalizedText)) {
    reasons.push('rambly_narrative');
  }

  const shouldForceReview = Boolean(
    hasMultipleEvents
    || (isLikelyDictated && wordCount >= 16 && categorySignals.count >= 2)
    || (isLikelyDictated && DICTATION_RAMBLY_PHRASE_REGEX.test(normalizedText))
  );

  if (shouldForceReview) {
    reasons.push('force_review');
  }

  return {
    originalText,
    normalizedText,
    isLikelyDictated,
    hasMultipleEvents,
    shouldForceReview,
    reasons,
  };
};

export default normalizeQuickCaptureTranscript;
